// Competencies.tsx
import React from 'react';
import { useState, Fragment } from 'react';
import { Anchor, Avatar, Button, Text, Paper, rem, SimpleGrid, Space, Stack, Modal, Group, Box, useMantineTheme } from '@mantine/core';
import { MainContentContainer, Heading } from '@clinicaltoolkits/universal-react-components';

const TeamMembersDemo = [
  {
    name: 'Susan Hendrickson',
    email: 'susanhendrickson@ailive.ca',
    role: 'Registered Psychologist',
    competencies: ['Assessment', 'Intervention', 'Everything', 'Other Things', 'More', 'Even More'],
    imageUrl: 'https://raw.githubusercontent.com/mantinedev/mantine/master/.demo/avatars/avatar-8.png',
    description: (
      <Stack gap="sm">
        <Text>
          <strong>Susan Hendrickson</strong> is a registered psychologist based in Calgary, Alberta, with over 25 years of experience in the field of child development and nearly two decades of practice as a registered psychologist. Throughout her career, she has been a leading voice in advancing <em>neurodiversity-affirming practices</em> and has committed much of her professional life to supporting children and families within the autism community.
        </Text>
        <Text>
          Susan has held prominent roles across multiple institutions, most notably serving as a psychology team lead, where she played a key role in mentoring and training emerging professionals in the field. Her expertise spans psychological assessment, intervention, and consultation, and she remains actively involved in both direct client care and systems-level program development.
        </Text>
        <Text>
          Over the years, Susan has contributed to the creation and refinement of numerous programs supporting children with diverse needs, hosted professional development workshops for clinicians, and collaborated across sectors to improve service delivery models in Alberta.
        </Text>
        <Text>
          Known for her deep commitment to the families she works with, Susan brings a rare combination of clinical insight, systems-level thinking, and heartfelt advocacy to her work—qualities that distinguish her within Alberta’s psychological community.
        </Text>
      </Stack>
    ),
  }
];

export const Team: React.FC = () => {
  const [bOpenedExpandedTeamCard, setBOpenedExpandedTeamCard] = useState(false);
  const theme = useMantineTheme();
  return (
    <MainContentContainer>
      <Heading
        order={2}
        headingText='Your Team'
        subheadingText='Because it takes a village to raise a child.'
      />
      <Space h={rem(60)} />
      <SimpleGrid cols={TeamMembersDemo.length < 3 ? TeamMembersDemo.length : 3} spacing="lg">
        <TeamCard
          name={TeamMembersDemo[0].name}
          email={TeamMembersDemo[0].email}
          role={TeamMembersDemo[0].role}
          competencies={TeamMembersDemo[0].competencies}
          imageUrl={TeamMembersDemo[0].imageUrl}
          primaryAction={
            <Button
                variant="default"
                fullWidth
                onClick={() => console.log(TeamMembersDemo[0].name)}
              >
                Send message
            </Button>
          }
          footer={
            <Anchor
              component="button"
              size="sm"
              c="blue.4"
              onClick={() => setBOpenedExpandedTeamCard(true)}
            >
              View profile
            </Anchor>
          }
        />
      </SimpleGrid>
      <Modal
        opened={bOpenedExpandedTeamCard}
        onClose={() => setBOpenedExpandedTeamCard(false)}
        bg={'var(--mantine-color-body)'}
        radius="md"
        withCloseButton={true}
        styles={{
          body: {
            backgroundColor: 'var(--mantine-color-body)',
            //padding: 0,
          },
          header: {
            backgroundColor: 'var(--mantine-color-body)',
            borderRadius: 0,
            minHeight: 0,
            paddingBottom: 0,
            paddingTop: rem(10)
          },
          close: {
            backgroundColor: 'transparent',
          },
        }}
      >
        <ExpandedTeamCard
          name={TeamMembersDemo[0].name}
          email={TeamMembersDemo[0].email}
          role={TeamMembersDemo[0].role}
          competencies={TeamMembersDemo[0].competencies}
          imageUrl={TeamMembersDemo[0].imageUrl}
          description={TeamMembersDemo[0].description}
          primaryAction={
            <Button
              variant="default"
              fullWidth
              onClick={() => console.log('Susan Hendrickson')}
            >
              Send message
            </Button>
          }
          withFrame={false}
        />
      </Modal>
    </MainContentContainer>
  );
}

export interface ITeamCardProps {
  name: string;
  email: string;
  role: string;
  competencies: string[];
  imageUrl: string;
  /** optional primary action rendered in the footer */
  primaryAction?: React.ReactNode;
  /** optional secondary stuff (tags, menu, etc.) */
  footer?: React.ReactNode;
}
export const TeamCard: React.FC<ITeamCardProps> = ({
  name,
  email,
  role,
  competencies,
  imageUrl,
  primaryAction,
  footer,
}) => {
  return (
    <Paper radius="md" withBorder p="lg" bg="var(--mantine-color-body)">
      <Avatar
        src="https://raw.githubusercontent.com/mantinedev/mantine/master/.demo/avatars/avatar-8.png"
        size={120}
        radius={120}
        mx="auto"
      />
      <Text ta="center" fz="lg" fw={500} mt="md">
        {name}
      </Text>
      <Text ta="center" c="dimmed" fz="sm">
        {role}
      </Text>
      <Competencies items={competencies} itemsPerLine={3} maxLines={1} maxWidth={"100%"} />
      <Stack gap="xs" mt="md">
        {primaryAction}
        {footer}
      </Stack>
    </Paper>
  );
};

export interface CompetenciesProps {
  items: string[];
  /** max items shown on a single line */
  itemsPerLine?: number;
  /** maximum number of lines before collapsing */
  maxLines?: number;
  /** css width (e.g. 260, '100%', '20rem') */
  maxWidth?: React.CSSProperties['maxWidth'];
  /** anchor label */
  seeMoreLabel?: string;
  seeLessLabel?: string;
}

export interface CompetenciesProps {
  items: string[];
  itemsPerLine?: number;      // default 3
  maxLines?: number;          // default 2
  maxWidth?: React.CSSProperties['maxWidth']; // default 265
  seeMoreLabel?: string;      // default 'see more'
  seeLessLabel?: string;      // default 'see less'
}

type Token = { type: 'word'; text: string } | { type: 'anchor' };

export const Competencies = ({
  items,
  itemsPerLine = 3,
  maxLines = 2,
  maxWidth = 265,
  seeMoreLabel = 'More',
  seeLessLabel = 'Less',
}: CompetenciesProps) => {
  const [expanded, setExpanded] = useState(false);

  // --- build visible token array ------------------------------------------
  const totalSlots = itemsPerLine * maxLines;

  const tokens: Token[] = expanded
    ? [
        ...items.map((text) => ({ type: 'word' as const, text })),
        { type: 'anchor' as const },
      ]
    : (() => {
        if (items.length <= totalSlots) {
          return items.map((text) => ({ type: 'word' as const, text }));
        }
        return [
          ...items.slice(0, totalSlots - 1).map((text) => ({
            type: 'word' as const,
            text,
          })),
          { type: 'anchor' as const },
        ];
      })();

  // --- split tokens into rows ---------------------------------------------
  const rows: Token[][] = [];
  for (let i = 0; i < tokens.length; i += itemsPerLine) {
    rows.push(tokens.slice(i, i + itemsPerLine));
  }

  // --- helpers -------------------------------------------------------------
  function renderToken(tok: Token, idx: number) {
    if (tok.type === 'word') return tok.text;
    // anchor
    const hidden = items.length - (totalSlots - 1); // only valid when collapsed
    const label = expanded ? seeLessLabel : `${seeMoreLabel} (${hidden})`;
    return (
      <Anchor
        key={`a-${idx}`}
        size="sm"
        c="blue.4"
        component="button"
        onClick={() => setExpanded((v) => !v)}
      >
        {label}
      </Anchor>
    );
  }

  // --- UI ------------------------------------------------------------------
  return (
    <Text
      ta="center"
      c="dimmed"
      fz="sm"
      maw={maxWidth}
      mx={'auto'}
      style={{
        display: 'block', // ← switch from inline-block to block
        width: 'fit-content', // keeps it only as wide as it needs to be
        lineHeight: 1.4,
      }}
    >
      {rows.map((row, ri) => (
        <Fragment key={ri}>
          {row.map((tok, ti) => (
            <Fragment key={ti}>
              {ti > 0 && ' • '}
              {renderToken(tok, ti)}
            </Fragment>
          ))}
          {ri < rows.length - 1 && <br />}
        </Fragment>
      ))}
    </Text>
  );
};

// Include description so that content can be populated in an expanded modal. Extends ITeamCardProps
export interface IExpandedTeamCardProps extends ITeamCardProps {
  description: React.ReactNode;
  withFrame?: boolean;
}
export const ExpandedTeamCard: React.FC<IExpandedTeamCardProps> = ({
  name,
  email,
  role,
  competencies,
  imageUrl,
  description,
  primaryAction,
  footer,
  withFrame = true,
}) => {
  const Wrapper = withFrame ? Paper : Box;
  return (
    <>
      <Avatar
        src={imageUrl}
        size={120}
        radius={120}
        mx="auto"
      />
      <Text ta="center" fz="lg" fw={500} mt="md">
        {name}
      </Text>
      <Text ta="center" c="dimmed" fz="sm">
        {role}
      </Text>
      <Competencies items={competencies} itemsPerLine={3} maxLines={1} maxWidth={265} />
      <Space h={rem(20)} />
      <Text ta="center" c="dimmed" fz="sm">
        {description}
      </Text>
      <Stack gap="xs" mt="md">
        {primaryAction}
        {footer}
      </Stack>
    </>
  );
}
